import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { formatTime12h, formatDate } from '../utils/timeFormat';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { 
  AlertTriangle, Calendar, Clock, Users, MapPin, ChevronRight, 
  Loader2, Zap, Briefcase, UserPlus, CheckCircle2
} from 'lucide-react';

/**
 * OpenShiftsPanel - Shows client's own shifts that need staffing attention
 * 
 * Purpose: "What on my account needs attention right now?"
 * Shows:
 * - Client's posted shifts not fully staffed
 * - Urgency indicators
 * - Positions remaining (using backup role logic)
 * - Quick Fill CTA
 */

const getUrgencyLevel = (shift) => {
  const shiftDate = new Date(shift.shift_date);
  const now = new Date();
  const daysUntil = Math.ceil((shiftDate - now) / (1000 * 60 * 60 * 24));
  const bookedCount = shift.booked_count || 0;
  const talentNeeded = shift.talent_needed || 1;
  const fillRate = bookedCount / talentNeeded;
  
  // High urgency: < 3 days away and < 50% filled
  if (daysUntil <= 3 && fillRate < 0.5) return 'high';
  // Medium urgency: < 7 days away and < 75% filled
  if (daysUntil <= 7 && fillRate < 0.75) return 'medium';
  // Low urgency: needs staffing but not urgent
  return 'low';
};

const UrgencyBadge = ({ level }) => {
  const config = {
    high: { 
      label: 'Urgent', 
      className: 'bg-red-100 text-red-700 border-red-200',
      icon: AlertTriangle
    },
    medium: { 
      label: 'Soon', 
      className: 'bg-amber-100 text-amber-700 border-amber-200',
      icon: Clock
    },
    low: { 
      label: 'Open', 
      className: 'bg-cyan-100 text-cyan-700 border-cyan-200',
      icon: UserPlus
    }
  };
  
  const { label, className, icon: Icon } = config[level] || config.low;
  
  return (
    <Badge variant="outline" className={`text-[10px] ${className}`}>
      <Icon className="w-3 h-3 mr-1" />
      {label}
    </Badge>
  );
};

const ShiftCard = ({ shift, onQuickFill, onViewShift }) => {
  const urgency = getUrgencyLevel(shift);
  const bookedCount = shift.booked_count || shift.bookings_count || 0;
  const talentNeeded = shift.talent_needed || 1;
  const positionsRemaining = talentNeeded - bookedCount;
  const hasBackupNeeded = shift.backup_needed && (shift.backup_filled || 0) < shift.backup_needed;
  const shiftId = shift.gig_id || shift.id;
  
  return (
    <div 
      className={`p-3 rounded-lg border transition-all cursor-pointer hover:shadow-sm ${
        urgency === 'high' 
          ? 'bg-red-50/50 border-red-200 hover:border-red-300'
          : urgency === 'medium'
            ? 'bg-amber-50/50 border-amber-200 hover:border-amber-300'
            : 'bg-white border-gray-200 hover:border-cyan-300'
      }`}
      onClick={() => onViewShift(shiftId)}
      data-testid={`open-shift-card-${shiftId}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-medium text-gray-900 text-sm truncate">
              {shift.venue_name || shift.event_name}
            </h4>
            <UrgencyBadge level={urgency} />
          </div>
          
          {/* Shift Details */}
          <div className="flex items-center gap-3 text-xs text-gray-500 mt-1.5">
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {formatDate(shift.shift_date)}
            </span>
            {shift.start_time && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatTime12h(shift.start_time)}
              </span>
            )}
            {shift.city && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {shift.city}
              </span>
            )}
          </div>
          
          {/* Staffing Status */}
          <div className="mt-2 space-y-1">
            {/* Main positions */}
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all ${
                    bookedCount >= talentNeeded 
                      ? 'bg-green-500' 
                      : urgency === 'high' 
                        ? 'bg-red-500' 
                        : 'bg-amber-500'
                  }`}
                  style={{ width: `${Math.min((bookedCount / talentNeeded) * 100, 100)}%` }}
                />
              </div>
              <span className={`text-xs font-medium ${
                positionsRemaining === 0 ? 'text-green-600' : 'text-gray-600'
              }`}>
                {bookedCount}/{talentNeeded}
              </span>
            </div>
            
            {/* Staffing message */}
            <p className="text-xs text-gray-600">
              {positionsRemaining > 0 ? (
                <span className={urgency === 'high' ? 'text-red-600 font-medium' : ''}>
                  {positionsRemaining} position{positionsRemaining > 1 ? 's' : ''} remaining
                </span>
              ) : hasBackupNeeded ? (
                <span className="text-cyan-600">
                  Fully staffed • Backup positions available
                </span>
              ) : (
                <span className="text-green-600 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Fully staffed
                </span>
              )}
            </p>
          </div>
        </div>
        
        {/* Quick Fill Button */}
        {(positionsRemaining > 0 || hasBackupNeeded) && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  className={`shrink-0 h-8 text-xs ${
                    urgency === 'high'
                      ? 'bg-red-500 hover:bg-red-600'
                      : 'bg-cyan-500 hover:bg-cyan-600'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onQuickFill(shiftId);
                  }}
                  data-testid={`quick-fill-btn-${shiftId}`}
                >
                  <Zap className="w-3 h-3 mr-1" />
                  Quick Fill
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left" className="text-xs">
                Instantly match with recommended talent
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      
      {/* Role Badge */}
      {shift.role_name && (
        <Badge variant="outline" className="mt-2 text-[10px] bg-gray-50">
          {shift.role_name}
        </Badge>
      )}
    </div>
  );
};

const OpenShiftsPanel = ({ maxShifts = 4 }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [shifts, setShifts] = useState([]);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    fetchOpenShifts();
  }, []);
  
  const fetchOpenShifts = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch client's shifts that need staffing
      const res = await api.get('/client/shifts?status=open&needs_staffing=true&limit=10');
      
      // Filter to shifts that actually need attention
      const needsAttention = (res.data.shifts || res.data || [])
        .filter(s => {
          const bookedCount = s.booked_count || s.bookings_count || 0;
          const positionsRemaining = s.talent_needed - bookedCount;
          const hasBackupNeeded = s.backup_needed && (s.backup_filled || 0) < s.backup_needed;
          return positionsRemaining > 0 || hasBackupNeeded;
        })
        .sort((a, b) => {
          // Sort by urgency: soonest date + lowest fill rate first
          const aDate = new Date(a.shift_date);
          const bDate = new Date(b.shift_date);
          const aBookedCount = a.booked_count || a.bookings_count || 0;
          const bBookedCount = b.booked_count || b.bookings_count || 0;
          const aFill = aBookedCount / a.talent_needed;
          const bFill = bBookedCount / b.talent_needed;
          
          // First by date (soonest first)
          if (aDate < bDate) return -1;
          if (aDate > bDate) return 1;
          // Then by fill rate (lowest first)
          return aFill - bFill;
        });
      
      setShifts(needsAttention);
    } catch (err) {
      console.error('Failed to fetch open shifts:', err);
      setError('Unable to load shifts');
    } finally {
      setLoading(false);
    }
  };
  
  const handleQuickFill = (gigId) => {
    // Navigate to shift with Quick Fill modal open
    navigate(`/client/shifts/${gigId}?quickfill=true`);
  };
  
  const handleViewShift = (gigId) => {
    navigate(`/client/shifts/${gigId}`);
  };
  
  if (loading) {
    return (
      <Card className="border-amber-100">
        <CardContent className="py-6 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-amber-500" />
        </CardContent>
      </Card>
    );
  }
  
  if (error) {
    return null;
  }
  
  // Count urgent shifts
  const urgentCount = shifts.filter(s => getUrgencyLevel(s) === 'high').length;
  
  return (
    <Card 
      className={`border ${
        urgentCount > 0 
          ? 'border-red-200 bg-gradient-to-br from-red-50/30 to-white' 
          : 'border-amber-200 bg-gradient-to-br from-amber-50/30 to-white'
      }`}
      data-testid="open-shifts-panel"
    >
      <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <Briefcase className={`w-4 h-4 ${urgentCount > 0 ? 'text-red-500' : 'text-amber-500'}`} />
          Shifts Needing Attention
          {shifts.length > 0 && (
            <Badge 
              variant="secondary" 
              className={`text-[10px] ${
                urgentCount > 0 
                  ? 'bg-red-100 text-red-700' 
                  : 'bg-amber-100 text-amber-700'
              }`}
            >
              {shifts.length}
            </Badge>
          )}
        </CardTitle>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate('/client/shifts')}
          className="text-gray-500 hover:text-gray-700"
        >
          All Shifts <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </CardHeader>
      
      <CardContent className="pt-0 px-4 pb-4">
        {shifts.length === 0 ? (
          <div className="text-center py-4 bg-green-50 rounded-lg border border-green-100">
            <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto mb-2" />
            <p className="text-sm font-medium text-green-700">All shifts are fully staffed!</p>
            <p className="text-xs text-green-600 mt-1">Great job staying on top of your staffing needs.</p>
            <Button 
              size="sm" 
              variant="outline" 
              className="mt-3 text-xs border-green-200 hover:bg-green-50"
              onClick={() => navigate('/client/shifts/create')}
            >
              Post New Shift
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {shifts.slice(0, maxShifts).map(shift => (
              <ShiftCard
                key={shift.gig_id || shift.id}
                shift={shift}
                onQuickFill={handleQuickFill}
                onViewShift={handleViewShift}
              />
            ))}
            
            {shifts.length > maxShifts && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="w-full text-xs text-gray-500 hover:text-gray-700"
                onClick={() => navigate('/client/shifts?filter=needs_staffing')}
              >
                View {shifts.length - maxShifts} more shifts
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default OpenShiftsPanel;
